import { useCallback, useMemo, useRef, useState } from 'react';
import { find, first, isEmpty, compact, map, forEach, findIndex, get } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';
import http from '@/utils/http';
import { useErrorTips } from '@/pages/aigc/hooks/useErrorTips';
import { getCOSPresigned } from '../../../../service/service';
import { useRequestController } from '../../../../Home/hooks/useRequestController.ts';
import { POSTER_FILE_SUFFIX, POSTER_FILE_TYPE } from '../constants';

/** 文件后缀类型：md_file 内容规划，png_file 海报预览 */
export type TFileSuffix = typeof POSTER_FILE_SUFFIX[keyof typeof POSTER_FILE_SUFFIX];

/** 文件项类型 */
export interface IMagicPosterFileItem {
  /** 文件唯一标识（即 latest_file.id） */
  id: string;
  /** wrapper 层 id（files[i].id），用于跨 md/png 两种后缀查询时关联同一任务 */
  group_id?: string;
  /** 文件名称 */
  name: string;
  /** COS 存储路径 */
  cos_url?: string;
  /** 文件后缀类型 */
  file_suffix?: TFileSuffix;
  [key: string]: any;
}

/** 文件类型：latest 获取最新文件，original 获取原始文件（用于重置） */
type TFileType = 'latest' | 'original';

/** 获取文件的请求参数 */
interface IFetchFilesParams {
  /** 会话 ID */
  sessionId: string;
  /** 文件类型：latest 获取最新文件（默认），original 获取原始文件（用于重置） */
  type?: TFileType;
  /** 目标文件 ID，用于在返回的全量文件列表中查找对应文件，不传则默认取第一个 */
  targetFileId?: string;
  /** 根据 file_suffix 筛选默认文件，不传则取第一个 */
  defaultFileSuffix?: TFileSuffix;
  /**
   * 旁路（只读）模式：
   * - 不更新 currentFile / fileList / signedUrlMapRef / loading 等主列表状态
   * - 直接用 http 拉数据并对目标文件签名一次
   * - 返回 { id, cosUrl, signedUrl } | null，而不是 signedUrlMapRef
   * 典型用途：在海报页签（PNG）点击生成时，需要拿"同 wrapper 下的 MD" id + 签名 URL
   */
  silent?: boolean;
  /**
   * silent 模式下的 wrapper 层 id（files[i].id），用于跨 md/png 查询时定位同一任务
   * 匹配失败时回退到列表第一个
   */
  groupId?: string;
}

/** silent 模式下的返回结果 */
export interface ISilentFetchResult {
  /** 目标文件 id（latest_file.id） */
  id: string;
  /** 目标文件 COS 原始路径 */
  cosUrl: string;
  /** 目标文件签名 URL */
  signedUrl: string;
}

/** Hook 配置选项 */
interface IUseMagicPosterFilesOptions {
  /** 当前步骤类型：framework 内容规划，poster 海报预览 */
  stepActive?: string;
}

/** Hook 返回类型 */
export interface IUseMagicPosterFilesReturn {
  /** 是否正在加载 */
  isLoading: boolean;
  /** 文件列表（转换后） */
  fileList: IMagicPosterFileItem[];
  /** 当前选中的文件 */
  currentFile: IMagicPosterFileItem | undefined;
  /** 当前文件的签名 URL（用于预览/下载） */
  currentFileSignedUrl: string;
  /** 当前文件的 markdown 内容（已解析） */
  currentFileContent: string;
  /**
   * 获取文件列表并解析
   * - type='latest' 获取最新（默认），type='original' 获取原始（重置）
   * - silent=true 时走旁路模式：不改动 UI 状态，仅返回 { id, cosUrl, signedUrl } | null
   */
  fetchFiles: (params: IFetchFilesParams) => Promise<any>;
  /** 切换到已有列表中的文件（从缓存获取签名URL） */
  switchFile: (file: IMagicPosterFileItem) => Promise<void>;
  /** 清空文件列表和当前文件状态 */
  clearFiles: () => void;
  /** 刷新当前文件的签名 URL（用于图片加载失败重试） */
  refreshCurrentFileUrl: () => Promise<void>;
}

/** 归一化文件后缀：将 jpg_file、jpeg_file 统一为 png_file */
export const normalizeFileSuffix = (fileSuffix?: string): TFileSuffix | undefined => {
  if (!fileSuffix) return fileSuffix as undefined;
  if (fileSuffix === 'jpg_file' || fileSuffix === 'jpeg_file') {
    return POSTER_FILE_SUFFIX.PNG;
  }
  return fileSuffix as TFileSuffix;
};

/**
 * 根据 cos_url 扩展名推断文件后缀：
 * 后端部分场景不返回 file_suffix 字段（latest_file.file_suffix 与 file_suffix 均为空），
 * 此时只能依据 COS 路径扩展名判别是内容规划（.md）还是海报图片（.png/.jpg/.jpeg/.webp）。
 */
const inferSuffixFromUrl = (cosUrl?: string): TFileSuffix | undefined => {
  if (!cosUrl) return undefined;
  // 去掉签名/查询参数，仅看路径扩展名
  const path = cosUrl.split('?')[0].toLowerCase();
  if (path.endsWith('.md')) return POSTER_FILE_SUFFIX.MD;
  if (
    path.endsWith('.png') ||
    path.endsWith('.jpg') ||
    path.endsWith('.jpeg') ||
    path.endsWith('.webp')
  ) {
    return POSTER_FILE_SUFFIX.PNG;
  }
  return undefined;
};

/**
 * 解析文件后缀：优先使用后端返回的 file_suffix（归一化后），
 * 缺失时回退到按 cos_url 扩展名推断，避免后端漏传 file_suffix 时整套后缀判断失效。
 */
const resolveFileSuffix = (rawSuffix?: string, cosUrl?: string): TFileSuffix | undefined =>
  normalizeFileSuffix(rawSuffix) ?? inferSuffixFromUrl(cosUrl);

/** 判断文件是否为 MD 类型 */
const isMdFileType = (fileSuffix?: string, cosUrl?: string): boolean =>
  fileSuffix === POSTER_FILE_SUFFIX.MD || (cosUrl?.endsWith('.md') ?? false);

/** 魔法海报文件列表管理 Hook */
export const useMagicPosterFiles = (
  _options: IUseMagicPosterFilesOptions = {}
): IUseMagicPosterFilesReturn => {
  const showErrorTips = useErrorTips();

  const [currentFile, setCurrentFile] = useState<IMagicPosterFileItem | undefined>(undefined);
  const [currentFileSignedUrl, setCurrentFileSignedUrl] = useState<string>('');
  const [currentFileContent, setCurrentFileContent] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const signedUrlMapRef = useRef<Record<string, string>>({});

  const requestData = useRequestController({
    method: 'POST',
    url: `/api/agent/get_excel_info`,
    manual: true,
    httpCfg: {
      rawData: true,
      ignoreCode: true,
    },
    onSuccess: (response) => {
      showErrorTips?.(response?.res_code);
    },
  });

  const { runAsync: originRunAsync, loading, data, mutate } = requestData;

  /** 将后端返回的文件数据转换为前端文件列表 */
  const fileList = useMemo<IMagicPosterFileItem[]>(() => {
    if (!data?.files || data?.files?.length === 0) {
      return [];
    }

    return data.files.map((file: any) => {
      const latestFile = file?.latest_file;
      return {
        ...latestFile,
        group_id: file?.id,
        date_time: file?.update_time,
        file_suffix: resolveFileSuffix(
          latestFile?.file_suffix || file?.file_suffix,
          latestFile?.cos_url || file?.cos_url,
        ),
      };
    });
  }, [data?.files]);

  /** 获取指定文件的签名 URL（优先缓存） */
  const getSignedUrl = useCallback(
    async (fileId: string, cosUrl?: string): Promise<string | null> => {
      const cachedUrl = signedUrlMapRef.current[fileId];
      if (cachedUrl) return cachedUrl;

      if (!cosUrl) return null;

      const srcList: any = await getCOSPresigned({
        cos_type: 'agentCos',
        url: [cosUrl],
      });
      const signedUrl: string | null = (first(srcList?.url) as string) || null;

      if (signedUrl) {
        signedUrlMapRef.current = {
          ...signedUrlMapRef.current,
          [fileId]: signedUrl,
        };
      }

      return signedUrl;
    },
    []
  );

  /** 下载并解析 MD 文件内容 */
  const downloadMdContent = useCallback(
    async (downloadUrl: string): Promise<string | null> => {
      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(`下载失败: ${response.status}`);
        }
        return await response.text();
      } catch (error) {
        console.error('下载或解析 MD 文件失败:', error);
        return null;
      }
    },
    []
  );

  /** 激活文件：设置签名 URL、解析内容、更新状态 */
  const activateFile = useCallback(
    async (
      signedUrl: string,
      file: IMagicPosterFileItem,
    ): Promise<void> => {
      setCurrentFileSignedUrl(signedUrl);

      const fileSuffix = file.file_suffix;
      const cosUrl = file.cos_url || '';

      if (isMdFileType(fileSuffix, cosUrl)) {
        const content = await downloadMdContent(signedUrl);
        setCurrentFileContent(content ?? '');
      } else {
        setCurrentFileContent('');
      }

      setCurrentFile(file);
    },
    [downloadMdContent]
  );

  /**
   * 旁路查询文件（silent 模式专用）：
   * - 直接用 http 调接口，不走 useRequestController，不影响主列表的 data / loading 等状态
   * - 仅对目标文件做一次签名
   * - 优先按 groupId 命中 wrapper，未命中则回退到第一个
   * 典型用途：海报页签（PNG）生成海报时，旁路获取同 wrapper 下的 md 文件 id + 签名 URL
   */
  const fetchFileSilent = useCallback(
    async (params: {
      sessionId: string;
      fileSuffix?: TFileSuffix;
      groupId?: string;
    }): Promise<ISilentFetchResult | null> => {
      const { sessionId, fileSuffix, groupId } = params;
      if (!sessionId) return null;

      try {
        const result: any = await http('/api/agent/get_excel_info', {
          method: 'POST',
          data: {
            trace_id: uuidv4(),
            session_id: sessionId,
            file_type: POSTER_FILE_TYPE,
            file_suffix: fileSuffix || POSTER_FILE_SUFFIX.PNG,
          },
          rawData: true,
          ignoreCode: true,
        } as any);

        const files = result?.files || [];
        if (isEmpty(files)) {
          console.warn('fetchFileSilent: 文件列表为空');
          return null;
        }

        const matched = groupId
          ? find(files, (f: any) => f?.id === groupId)
          : undefined;
        const targetWrapper = matched || first(files);
        const targetFile = targetWrapper?.latest_file;
        if (!targetFile?.id || !targetFile?.cos_url) {
          console.warn('fetchFileSilent: 未找到可用文件');
          return null;
        }

        const srcList: any = await getCOSPresigned({
          cos_type: 'agentCos',
          url: [targetFile.cos_url],
        });
        const signedUrl: string | null = (first(srcList?.url) as string) || null;
        if (!signedUrl) {
          console.warn('fetchFileSilent: 签名失败');
          return null;
        }

        return {
          id: targetFile.id,
          cosUrl: targetFile.cos_url,
          signedUrl,
        };
      } catch (error) {
        console.error('fetchFileSilent 失败:', error);
        return null;
      }
    },
    [],
  );

  /**
   * 获取文件列表并解析
   * - type='latest'：获取最新文件（默认）
   * - type='original'：获取原始文件（用于重置）
   * - silent=true：走旁路模式（见 fetchFileSilent），不改动 UI 状态
   *
   * 流程：
   * 1. 调用后端接口获取文件列表
   * 2. 根据 targetFileId 定位目标文件（未传则取第一个）
   * 3. 根据 type 选择 latest_file 或 original_file
   * 4. 批量获取 COS 签名 URL
   * 5. 缓存签名 URL 映射（仅 latest 模式）
   * 6. 激活目标文件（设置签名 URL、解析内容、更新状态）
   */
  const fetchFiles = useCallback(
    async (params: IFetchFilesParams) => {
      const { sessionId, targetFileId, type = 'latest', defaultFileSuffix, silent = false, groupId } = params;

      // 旁路模式分流：逻辑抽到独立方法，避免主流程夹杂分支
      if (silent) {
        // 必须返回 silentResult（{ id, cosUrl, signedUrl } | null）：
        // handleGeneratePoster 依赖它解析 input_md_id / input_md_url，
        // 之前误写成 return null 会导致从海报预览页重新生成时把 PNG 当 md 输入，画不出海报。
        return fetchFileSilent({
          sessionId,
          fileSuffix: defaultFileSuffix,
          groupId,
        });
      }

      const isOriginal = type === 'original';
      setIsProcessing(true);

      try {
        // 1. 调用后端接口获取文件列表
        const result = await originRunAsync({
          trace_id: uuidv4(),
          session_id: sessionId,
          file_type: POSTER_FILE_TYPE,
          file_suffix: defaultFileSuffix || POSTER_FILE_SUFFIX.PNG,
        });

        const files = result?.files || [];
        if (isEmpty(files)) {
          console.warn(isOriginal ? '重置失败：未找到魔法海报文件' : '未找到魔法海报文件');
          return result;
        }

        // 2. 先按请求的后缀类型筛选候选 wrapper：
        //    海报预览页签只认 png，内容规划页签只认 md。
        //    后端可能漏传 file_suffix，此处用 resolveFileSuffix 按 cos_url 扩展名兜底，
        //    避免「请求 png 时却用上一步的 md 文件 id 命中 md wrapper」导致把 md 当海报塞进 <img> 加载失败。
        const suffixFiles = defaultFileSuffix
          ? files.filter((file: any) =>
            resolveFileSuffix(
              file?.latest_file?.file_suffix || file?.file_suffix,
              file?.latest_file?.cos_url || file?.cos_url,
            ) === defaultFileSuffix
          )
          : files;
        // 该后缀无匹配时回退到全量列表，保持兼容
        const candidateFiles = isEmpty(suffixFiles) ? files : suffixFiles;

        // 在候选范围内根据 targetFileId 查找匹配项（匹配 latest_file 或 original_file 的 id）
        const matchedFile = targetFileId
          ? find(candidateFiles, (file: any) => {
            const latestFileId = file?.latest_file?.id;
            const originalFileId = file?.original_file?.id;
            return latestFileId === targetFileId || originalFileId === targetFileId;
          })
          : undefined;
        // 未匹配则回退到候选中的第一个文件
        const targetFileWrapper = matchedFile || first(candidateFiles);
        // 3. 根据 type 选择 latest_file 或 original_file
        const fileKey = isOriginal ? 'original_file' : 'latest_file';
        const targetFile = targetFileWrapper?.[fileKey];

        if (!targetFile) {
          console.warn(isOriginal ? '重置失败：未找到原始文件' : '未找到目标文件');
          return result;
        }

        // 4. 收集需要签名的 COS URL：重置模式只取目标文件，最新模式取全部
        const fileUrls = isOriginal
          ? compact([targetFile?.cos_url])
          : compact(map(files, (task: any) => task?.latest_file?.cos_url));

        if (isEmpty(fileUrls)) {
          console.warn(isOriginal ? '重置失败：原始文件无 cos_url' : '未找到文件 COS 路径');
          return result;
        }

        // 批量获取 COS 签名 URL
        const srcList: any = await getCOSPresigned({
          cos_type: 'agentCos',
          url: fileUrls,
        });

        // 从签名结果中定位目标文件对应的签名 URL
        const targetCosUrl = targetFile?.cos_url;
        const matchedIndex = Math.max(0, findIndex(fileUrls, (url: string) => url === targetCosUrl));
        const signedUrl = get(srcList, ['url', matchedIndex]) as string | undefined;

        if (!signedUrl) {
          return result;
        }

        // 5. 仅 latest 模式下缓存所有文件的签名 URL 映射（供 switchFile 使用）
        if (!isOriginal && srcList?.url) {
          const newSignedUrlMap: Record<string, string> = {};
          forEach(files, (file: any, index: number) => {
            const latestFile = file?.latest_file;
            if (latestFile?.id && srcList.url[index]) {
              newSignedUrlMap[latestFile.id] = srcList.url[index];
            }
          });
          signedUrlMapRef.current = newSignedUrlMap;
        }

        // 6. 构建文件项并激活
        const fileItem: IMagicPosterFileItem = {
          ...targetFile,
          group_id: targetFileWrapper?.id,
          file_suffix: resolveFileSuffix(
            targetFile?.file_suffix || targetFileWrapper?.file_suffix,
            targetFile?.cos_url || targetFileWrapper?.cos_url,
          ),
        };

        if (isOriginal) {
          // 重置操作：只获取文件内容替换显示数据，不更新签名 URL 和当前文件
          // 因为保存时应始终保存到最新文件的路径上，重置只替换显示数据
          const fileSuffix = fileItem.file_suffix;
          const cosUrl = fileItem.cos_url || '';
          if (isMdFileType(fileSuffix, cosUrl)) {
            const content = await downloadMdContent(signedUrl);
            setCurrentFileContent(content ?? '');
          } else {
            setCurrentFileContent('');
          }
        } else {
          // 正常加载：完整激活文件（设置签名 URL、解析内容、更新当前文件）
          await activateFile(signedUrl, fileItem);
        }

        // 返回 result，供调用方从中提取文件 ID（用于设置 fileActive 缓存）
        return result;
      } catch (error) {
        console.error(isOriginal ? '重置失败:' : '获取魔法海报文件列表失败:', error);
        return null;
      } finally {
        setIsProcessing(false);
      }
    },
    [originRunAsync, activateFile, downloadMdContent, fetchFileSilent]
  );

  /** 切换到已有列表中的文件（从缓存获取签名 URL） */
  const switchFile = useCallback(
    async (file: IMagicPosterFileItem): Promise<void> => {
      if (!file?.id) return;

      setIsProcessing(true);
      try {
        const signedUrl = await getSignedUrl(file.id, file.cos_url);
        if (signedUrl) {
          await activateFile(signedUrl, file);
        } else {
          setCurrentFile(file);
        }
      } catch (error) {
        console.error('切换文件失败:', error);
      } finally {
        setIsProcessing(false);
      }
    },
    [getSignedUrl, activateFile]
  );

  /** 清空文件列表和当前文件状态 */
  const clearFiles = useCallback(() => {
    mutate({ files: [] });
    setCurrentFile(undefined);
  }, [mutate]);

  /** 刷新当前文件的签名 URL（用于图片加载失败重试） */
  const refreshCurrentFileUrl = useCallback(async () => {
    if (!currentFile?.id || !currentFile?.cos_url) return;

    setIsProcessing(true);
    try {
      // 清除缓存，强制重新获取
      delete signedUrlMapRef.current[currentFile.id];
      
      const srcList: any = await getCOSPresigned({
        cos_type: 'agentCos',
        url: [currentFile.cos_url],
      });
      const signedUrl: string | null = (first(srcList?.url) as string) || null;

      if (signedUrl) {
        signedUrlMapRef.current[currentFile.id] = signedUrl;
        setCurrentFileSignedUrl(signedUrl);
      }
    } catch (error) {
      console.error('刷新签名 URL 失败:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [currentFile]);

  /** 综合加载状态：接口请求中 或 文件处理中 */
  const isLoading = useMemo(() => loading || isProcessing, [loading, isProcessing]);

  return {
    isLoading,
    fileList,
    currentFile,
    currentFileSignedUrl,
    currentFileContent,
    fetchFiles,
    switchFile,
    clearFiles,
    refreshCurrentFileUrl,
  };
};

export default useMagicPosterFiles;
